import React, { useEffect, useRef, useState } from "react";
import { Badge, Card, Col, Modal, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { rockTasksApi } from "../../services/rockTasksApi";
import ErrorAlert from "../common/ErrorAlert";
import LoadingBlock from "../common/LoadingBlock";
import TaskRecognitionResult from "./TaskRecognitionResult";

const SOURCE_BADGE = {
  rock_identifier: "primary",
  openai: "info",
  rockinapp: "secondary"
};

function InfoRow({ label, value }) {
  return (
    <tr>
      <td className="text-muted small" style={{ width: 180, whiteSpace: "nowrap" }}>
        {label}
      </td>
      <td style={{ wordBreak: "break-all" }}>{value ?? "—"}</td>
    </tr>
  );
}

function TaskDetailView({ taskId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [task, setTask] = useState(null);
  const [taskIdCopied, setTaskIdCopied] = useState(false);
  const taskIdCopyTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!taskId) return;
      setLoading(true);
      setError(null);
      setTask(null);
      try {
        const data = await rockTasksApi.getTaskDetail(taskId);
        if (!cancelled) setTask(data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  useEffect(() => {
    return () => {
      if (taskIdCopyTimerRef.current) {
        clearTimeout(taskIdCopyTimerRef.current);
        taskIdCopyTimerRef.current = null;
      }
    };
  }, []);

  if (loading) return <LoadingBlock label="Loading task..." />;
  if (error) return <ErrorAlert error={error} title="Failed to load task" />;
  if (!task) return null;

  async function copyTaskId() {
    const text = task?.task_id;
    if (!text) return;
    try {
      if (typeof navigator?.clipboard?.writeText === "function") {
        await navigator.clipboard.writeText(text);
        setTaskIdCopied(true);
        if (taskIdCopyTimerRef.current) clearTimeout(taskIdCopyTimerRef.current);
        taskIdCopyTimerRef.current = setTimeout(() => setTaskIdCopied(false), 1500);
      }
    } catch {
      // ignore (clipboard may be blocked in some contexts)
    }
  }

  const hasResult = task.status === "done" || task.status === "completed";
  const hasStored =
    task.stored_result != null || task.result_data != null;

  return (
    <Row className="row-deck row-cards g-3">
      <Col xs={12}>
        <Card>
          <Card.Header>
            <Card.Title className="mb-0">Task Info</Card.Title>
          </Card.Header>
          <Card.Body className="p-0">
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <InfoRow label="ID" value={task.id} />
                <InfoRow
                  label="Task UUID"
                  value={
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{taskIdCopied ? "Copied" : "Click to copy"}</Tooltip>}
                    >
                      <code
                        className="small"
                        role="button"
                        tabIndex={0}
                        onClick={copyTaskId}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") copyTaskId();
                        }}
                        style={{ cursor: "pointer" }}
                        title="Click to copy"
                      >
                        {task.task_id}
                      </code>
                    </OverlayTrigger>
                  }
                />
                <InfoRow
                  label="Source"
                  value={
                    <Badge bg={SOURCE_BADGE[task.source] ?? "secondary"}>
                      {task.source}
                    </Badge>
                  }
                />
                <InfoRow
                  label="Status"
                  value={
                    <Badge bg={task.status === "done" ? "success" : "danger"}>
                      {task.status}
                    </Badge>
                  }
                />
                <InfoRow label="Device ID" value={task.device_id} />
                <InfoRow
                  label="Created"
                  value={task.created_at ? new Date(task.created_at).toLocaleString() : null}
                />
                <InfoRow
                  label="Updated"
                  value={task.updated_at ? new Date(task.updated_at).toLocaleString() : null}
                />
              </tbody>
            </table>
          </Card.Body>
        </Card>
      </Col>

      {task.user_photo ? (
        <Col xs={12}>
          <Card>
            <Card.Header>
              <Card.Title className="mb-0">User Photo</Card.Title>
            </Card.Header>
            <Card.Body>
              <img
                alt="User upload"
                src={task.user_photo}
                style={{ maxWidth: "100%", maxHeight: 360, borderRadius: 8, objectFit: "contain" }}
              />
            </Card.Body>
          </Card>
        </Col>
      ) : null}

      {task.status === "failed" ? (
        <Col xs={12}>
          <Card border="danger">
            <Card.Header className="bg-danger text-white">
              <Card.Title className="mb-0">Error Details</Card.Title>
            </Card.Header>
            <Card.Body>
              <pre className="mb-0 small text-danger" style={{ whiteSpace: "pre-wrap" }}>
                {task.error_message ?? "No error message provided."}
              </pre>
            </Card.Body>
          </Card>
        </Col>
      ) : null}

      {hasResult && hasStored ? (
        <Col xs={12}>
          <TaskRecognitionResult task={task} />
        </Col>
      ) : null}
    </Row>
  );
}

export default function TaskDetailModal({ show, onHide, taskId, taskNumericId }) {
  const titleId = taskNumericId != null ? `#${taskNumericId}` : taskId;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Task Detail{" "}
          {titleId ? (
            <span className="text-muted small font-monospace">({titleId})</span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{taskId ? <TaskDetailView taskId={taskId} /> : null}</Modal.Body>
    </Modal>
  );
}
